import * as React from 'react'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  PerformanceCyclesDataTableDocument,
  PerformanceGoalsMetricGroupDataTableDocument
} from 'generated/resources/datatable.graphql'
import {
  PerformanceCyclesCycleFilterDocument,
  PerformanceGoalsIndexPageDocument
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { useFeatures } from 'modules/core/hooks/useFeatures'

import { CreateMetricGroupModal } from 'modules/goals/components/CreateMetricGroupModal'
import { KeyResultsCard } from 'modules/goals/components/KeyResultsCard'
import { GoalsTable } from 'modules/goals/pages/TeamDashboard/_GoalsTable'
import { TableHeader } from 'modules/goals/pages/TeamDashboard/_TableHeader'
import { TeamTable } from 'modules/goals/pages/TeamDashboard/_TeamTable'
import { GoalsDataCollection } from 'modules/goals/revamp/components/GoalsDataCollection'
import { PeopleDataCollection } from 'modules/goals/revamp/components/PeopleDataCollection'
import { YourProgressCard } from 'modules/goals/revamp/components/YourProgressCard'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { CreateCycleModal } from 'modules/performance_cycles/components/CreateCycleModal'
import { PerformanceCyclesTable } from 'modules/performance_cycles/components/PerformanceCyclesTable'
import { useCycleFilter } from 'modules/performance_cycles/hooks/useCycleFilter'

const GoalCreateDialog = React.lazy(() =>
  import('modules/goals/revamp/components/GoalCreateDialog').then((m) => ({
    default: m.GoalCreateDialog
  }))
)

export type ValidSection = ['people', 'groups'][number]
type ValidModal = ['createMetricGroup'][number]
const modalQueryParam = 'modal' as const

export const getValidSection = ({
  section,
  canSeeOtherEmployees
}: {
  section?: ValidSection
  canSeeOtherEmployees: boolean
}): ValidSection => {
  const defaultSection = canSeeOtherEmployees ? 'people' : 'groups'
  if (!section) return defaultSection
  return section === 'people' && !canSeeOtherEmployees ? 'groups' : section
}

const TeamDashboard = ({ section }: { section?: ValidSection }) => {
  const { navigation } = useNavigation()
  const { employee } = useCurrentSession()
  const { has_dev_goals_revamp, loadingFeatures } = useFeatures(['dev_goals_revamp'])
  const { loading: loadingCycles, props: cycleFilterProps } = useCycleFilter()
  const cycleIds = compact([cycleFilterProps.value])

  const { data, loading, error } = useQuery(PerformanceGoalsIndexPageDocument, {
    variables: {
      cycleIds: cycleIds,
      currentEmployeeId: employee?.id
    }
  })

  const [openCreateCycleModal, setOpenCreateCycleModal] = useState(false)
  const [showGoalCreateDialog, setShowGoalCreateDialog] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const { search, debouncedSearch, onSearch, clearSearch } = useDebouncedSearch()
  const [onlyDirectReports, setOnlyDirectReports] = useState(true)

  const currentEmployee = data?.apiCore?.currentsConnection.nodes[0]?.employee

  const canCreateCycles = Boolean(data?.performanceCycles._policies.cycleManage.can)
  const canSeeOtherEmployees = Boolean(data?.goals?.employeesConnection?.totalCount)

  const currentSection = getValidSection({ section, canSeeOtherEmployees })

  const navigate = useNavigate()

  const currentGoalsEmployee = currentEmployee?.goalsEmployee
  const canCreateMetricGroup = Boolean(data?.goals?._policies?.createMetricGroup.can)

  const currentEmployeeGoalsPath = currentGoalsEmployee
    ? navigation.goals.employees.employee.fullPath({
        id: currentGoalsEmployee.id
      })
    : null

  const isSidepanelOpen = searchParams.get('cyclePanel') === 'open'

  const isModalOpen = (modal: ValidModal) => searchParams.get(modalQueryParam) === modal
  const openModal = (modal: ValidModal) =>
    setSearchParams({
      [modalQueryParam]: modal
    })
  const closeModal = () => setSearchParams({})

  const handleCreateGoalClick = () => {
    if (has_dev_goals_revamp) {
      setShowGoalCreateDialog(true)
    } else {
      openModal('createMetricGroup')
    }
  }

  const tableHeaderProps = {
    cycleFilterProps,
    createMetricGroup: {
      can: canCreateMetricGroup,
      onClick: handleCreateGoalClick
    },
    manageCycles: {
      can: canCreateCycles,
      onClick: () => setSearchParams({ cyclePanel: 'open' })
    },
    search: { value: search, onChange: onSearch, clear: clearSearch },
    ...(currentSection === 'people'
      ? { onlyDirectReports: { value: onlyDirectReports, onChange: setOnlyDirectReports } }
      : {})
  }

  const goalsTableProps = {
    canCreateMetricGroup: canCreateMetricGroup,
    search: debouncedSearch,
    cycleIds: cycleIds,
    noCycleSelected: cycleFilterProps.noCycleSelected,
    onCreate: handleCreateGoalClick
  }

  const teamTableProps = {
    search,
    onlyDirectReports,
    cycleIds,
    noCycleSelected: cycleFilterProps.noCycleSelected
  }

  return (
    <ResponsivePage.Body
      loading={loading || loadingCycles || loadingFeatures}
      error={error}
      header={{
        widget:
          currentGoalsEmployee && currentEmployeeGoalsPath ? (
            has_dev_goals_revamp ? (
              <YourProgressCard
                goalsEmployee={currentGoalsEmployee}
                to={currentEmployeeGoalsPath}
              />
            ) : (
              <KeyResultsCard
                isYou
                goalsEmployee={currentGoalsEmployee}
                to={currentEmployeeGoalsPath}
              />
            )
          ) : undefined
      }}
    >
      {canSeeOtherEmployees ? (
        <>
          {'people' === currentSection && (
            <Box paddingTop="s16">
              {has_dev_goals_revamp ? (
                <PeopleDataCollection
                  canManageCycles={canCreateCycles}
                  onManageCycles={() => setSearchParams({ cyclePanel: 'open' })}
                  canCreateGoal={canCreateMetricGroup}
                  onCreate={handleCreateGoalClick}
                />
              ) : (
                <>
                  <TableHeader {...tableHeaderProps} />
                  <TeamTable {...teamTableProps} />
                </>
              )}
            </Box>
          )}
          {'groups' === currentSection && (
            <Box paddingTop="s16">
              {has_dev_goals_revamp ? (
                <GoalsDataCollection
                  canCreateMetricGroup={canCreateMetricGroup}
                  canManageCycles={canCreateCycles}
                  onCreate={handleCreateGoalClick}
                  onManageCycles={() => setSearchParams({ cyclePanel: 'open' })}
                />
              ) : (
                <>
                  <TableHeader {...tableHeaderProps} />
                  <GoalsTable {...goalsTableProps} />
                </>
              )}
            </Box>
          )}
        </>
      ) : (
        <>
          {has_dev_goals_revamp ? (
            <GoalsDataCollection
              canCreateMetricGroup={canCreateMetricGroup}
              canManageCycles={canCreateCycles}
              onCreate={handleCreateGoalClick}
              onManageCycles={() => setSearchParams({ cyclePanel: 'open' })}
            />
          ) : (
            <>
              <TableHeader {...tableHeaderProps} />
              <GoalsTable {...goalsTableProps} />
            </>
          )}
        </>
      )}

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wideXL"
        title={i18n.t('performance_goals.cycles.manage_cycles')}
      >
        <Box gap="s16">
          <PerformanceCyclesTable />
          <Button iconName="add" onClick={() => setOpenCreateCycleModal(true)}>
            {i18n.t('performance_cycles.new_cycle')}
          </Button>
        </Box>

        {openCreateCycleModal && (
          <CreateCycleModal
            onClose={() => setOpenCreateCycleModal(false)}
            refetchQueries={[
              PerformanceCyclesDataTableDocument,
              PerformanceCyclesCycleFilterDocument
            ]}
          />
        )}
      </Sidepanel>

      {showGoalCreateDialog && has_dev_goals_revamp && (
        <React.Suspense fallback={null}>
          <GoalCreateDialog
            onClose={() => setShowGoalCreateDialog(false)}
            onSuccess={({ id }) =>
              navigate(navigation.goals.groups.group.fullPath({ id: id.toString() }))
            }
            refetchQueries={[PerformanceGoalsMetricGroupDataTableDocument]}
          />
        </React.Suspense>
      )}

      {isModalOpen('createMetricGroup') && !has_dev_goals_revamp && (
        <CreateMetricGroupModal
          onClose={closeModal}
          onSubmit={({ id }) =>
            navigate(navigation.goals.groups.group.fullPath({ id: id.toString() }))
          }
          refetchQueries={[PerformanceGoalsMetricGroupDataTableDocument]}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default TeamDashboard
